import moment from 'moment';
import React, { ReactNode, useContext, useMemo, useState } from 'react';

import {
  replaceParams,
  getNestedValues
} from 'utils/helpers/translationHelper';
import {
  JSONObject,
  TranslateFunction,
  Translations
} from 'utils/interfaces/Translations';

import { APP_LANGUAGE, LANGUAGE_CONFIG } from 'config/i18nConfig';

import bg from 'i18n/bg.json';
import en from 'i18n/en.json';

const TRANSLATIONS_DICTIONARY: JSONObject = {
  en,
  bg
};

const TranslationsContext = React.createContext<Translations>({
  currentLanguage: '',
  setCurrentLanguage: () => {},
  translate: () => ''
});

interface TranslationsProviderProps {
  readonly children: ReactNode;
}

export const TranslationsProvider = ({
  children
}: TranslationsProviderProps) => {
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem(APP_LANGUAGE) || LANGUAGE_CONFIG.defaultLanguage.code
  );

  moment.locale(
    localStorage.getItem(APP_LANGUAGE) || LANGUAGE_CONFIG.defaultLanguage.code
  );

  const setNewLanguage = (newLanguage: string) => {
    if (TRANSLATIONS_DICTIONARY[newLanguage]) {
      localStorage.setItem(APP_LANGUAGE, newLanguage);
      setCurrentLanguage(newLanguage);
      moment.locale(newLanguage);
    } else {
      localStorage.setItem(APP_LANGUAGE, LANGUAGE_CONFIG.defaultLanguage.code);
      setCurrentLanguage(LANGUAGE_CONFIG.defaultLanguage.code);
      moment.locale(LANGUAGE_CONFIG.defaultLanguage.code);
    }
  };

  const translate: TranslateFunction = (module, params) => {
    const language = TRANSLATIONS_DICTIONARY[currentLanguage];
    const label = getNestedValues(language, module);

    if (label === undefined) {
      return module;
    }

    const templateLabel = params && replaceParams(label, params);

    return templateLabel || label;
  };

  const context = useMemo(
    () => ({
      currentLanguage,
      setCurrentLanguage: setNewLanguage,
      translate
    }),

    [currentLanguage]
  );

  return (
    <TranslationsContext.Provider value={context}>
      {children}
    </TranslationsContext.Provider>
  );
};

export const useTranslations = (): Translations => {
  const context = useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error(
      'useTranslations must be used with-in a TranslationsProvider'
    );
  }
  return context;
};
